import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getServiceById, SERVICE_DISCLAIMER } from "@/content/serviceDescriptions";
import { HelpCircle, AlertCircle, CheckCircle2, XCircle, Lock, CreditCard, Loader2 } from "lucide-react";
import { useState } from "react";

/**
 * ServiceInfoModal - Canonical Modal for Service Descriptions (v1.0)
 * 
 * Renders verbatim canonical content. No paraphrasing.
 * 
 * Modal sections (in order):
 * 1. Purpose of This Service
 * 2. What This Service Actually Delivers
 * 3. Evidence Used
 * 4. Final Deliverable
 * 5. Important Clarification (B/C only)
 * 6. What This Service Does NOT Do
 * 7. When This Service Makes Sense
 * 8. Fixed Price (GATED - shows placeholder when hidden)
 * 
 * Props:
 * - serviceId: The service ID to display
 * - isOpen: Whether the modal is open
 * - onClose: Function to close the modal
 * - pricingVisible: Whether to show actual prices (post-evaluation + SER)
 * - dynamicPrices: Object with dynamic prices from admin config { SERVICE_A: { price: 4950, display: "$4,950" }, ... }
 * - showPurchaseCTA: Whether to show the "Proceed to Payment" button
 * - onPurchase: Callback when user clicks purchase CTA
 * - isPurchasing: Whether purchase is in progress
 */
const ServiceInfoModal = ({ 
  serviceId, 
  isOpen, 
  onClose, 
  pricingVisible = false, 
  dynamicPrices = {},
  showPurchaseCTA = false,
  onPurchase = null,
  isPurchasing = false
}) => {
  const service = serviceId ? getServiceById(serviceId) : null;

  if (!service) {
    return null;
  }

  // Get dynamic price if available, otherwise fall back to static
  const dynamicPrice = dynamicPrices[serviceId];
  const priceDisplay = dynamicPrice 
    ? `${dynamicPrice.display} (fixed, prepaid)` 
    : service.pricing.displayLabelWhenVisible;

  const handlePurchase = () => {
    if (onPurchase) {
      onPurchase(serviceId);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-navy-900 border-slate-700"
        data-testid={`service-modal-${serviceId}`}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <HelpCircle className="w-5 h-5 text-primary" />
            </div>
            {service.title}
          </DialogTitle>
          <DialogDescription className="text-slate-400 mt-2 text-base">
            {service.headline}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {/* Render modal sections in canonical order */}
          {service.modalSections.map((section) => (
            <ModalSection key={section.id} section={section} />
          ))}

          {/* Fixed Price Section (GATED) */}
          <div className="bg-primary/10 rounded-lg p-4 border border-primary/30">
            <div className="flex items-center gap-2 mb-2">
              {pricingVisible ? (
                <CheckCircle2 className="w-5 h-5 text-primary" />
              ) : (
                <Lock className="w-5 h-5 text-slate-500" />
              )}
              <h4 className="font-heading font-semibold text-white text-lg">
                Fixed Price
              </h4>
            </div>
            {pricingVisible ? (
              <>
                <p className="text-white text-xl font-bold" data-testid={`modal-price-visible-${serviceId}`}>
                  {priceDisplay}
                </p>
                <p className="text-slate-400 text-sm mt-1">
                  Prepaid, fixed-scope engagement
                </p>
              </>
            ) : (
              <p className="text-slate-400 text-sm" data-testid={`modal-price-hidden-${serviceId}`}>
                {service.pricing.displayLabelWhenHidden}
              </p>
            )}
          </div>

          {/* Purchase CTA (only shown when showPurchaseCTA is true and pricing is visible) */}
          {showPurchaseCTA && pricingVisible && onPurchase && (
            <div className="pt-2">
              <Button
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-4 text-base"
                onClick={handlePurchase}
                disabled={isPurchasing}
                data-testid={`modal-purchase-btn-${serviceId}`}
              >
                {isPurchasing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating Checkout...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Proceed to Payment
                  </>
                )}
              </Button>
              <p className="text-slate-500 text-xs text-center mt-2">
                You'll be redirected to our secure payment partner (Stripe)
              </p>
            </div>
          )}

          {/* Footer Disclaimer (Non-removable) */}
          <div className="pt-4 border-t border-slate-800">
            <p className="text-slate-500 text-xs text-center">
              {SERVICE_DISCLAIMER}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * ModalSection - Renders a single canonical modal section
 */
const ModalSection = ({ section }) => {
  // Determine section styling based on section type
  const getSectionStyle = (sectionId) => {
    switch (sectionId) {
      case "clarification":
        return {
          wrapper: "bg-blue-500/10 rounded-lg p-4 border border-blue-500/30",
          icon: <AlertCircle className="w-5 h-5 text-blue-400" />,
          headingColor: "text-blue-400"
        };
      case "not_do":
        return {
          wrapper: "bg-red-500/5 rounded-lg p-4 border border-red-500/20",
          icon: <XCircle className="w-5 h-5 text-red-400" />,
          headingColor: "text-red-400"
        };
      case "deliverable":
        return {
          wrapper: "bg-green-500/5 rounded-lg p-4 border border-green-500/20",
          icon: <CheckCircle2 className="w-5 h-5 text-green-400" />,
          headingColor: "text-green-400"
        };
      default:
        return {
          wrapper: "",
          icon: null,
          headingColor: "text-white"
        };
    }
  };

  const style = getSectionStyle(section.id);

  return (
    <div className={style.wrapper}>
      <div className="flex items-center gap-2 mb-3">
        {style.icon}
        <h4 className={`font-heading font-semibold text-sm uppercase tracking-wide ${style.headingColor}`}>
          {section.heading}
        </h4>
      </div>
      <div className="space-y-2">
        {section.body.map((paragraph, index) => (
          <p 
            key={index} 
            className={`text-slate-300 leading-relaxed ${paragraph.startsWith("•") ? "pl-4" : ""}`}
          >
            {paragraph}
          </p>
        ))}
      </div>
    </div>
  );
};

export default ServiceInfoModal;
